import { StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import './styles/index.css';
import i18n, { i18nReady } from './i18n';
import App from './App.tsx';
import { QueryProvider } from './providers/QueryProvider';
import { LanguageProvider } from './contexts/LanguageContext';
import { I18nextProvider } from 'react-i18next';
import { registerSW } from 'virtual:pwa-register';
import { initSentry } from './lib/sentry';
import { AppErrorBoundary } from './components/AppErrorBoundary';
import {
  clearReloadGuard,
  isChunkLoadError,
  stripCacheBustParam,
  tryRecoverFromChunkError,
} from './lib/chunk-recovery';
import { PageLoader } from './components/LazyLoad';
import { deferUpdate } from './lib/pending-update';
import { hasStoredSession } from './lib/session';
import { startSessionReads } from './api/auth.api';

// Must be initialised before any other code so breadcrumbs are captured from the start
initSentry();

// A recovery reload may have added a cache-busting param; drop it before the
// router reads the URL.
stripCacheBustParam();

// Vite's preloader fires this when a page's JS/CSS dependency 404s after a
// deploy. The reload starts here; the event is NOT prevented. Prevented, Vite
// resolves the import with `undefined` instead of rejecting, and the page's
// `.then((m) => m.Page)` threw a TypeError that nothing recognises as a stale
// chunk: the route boundary showed a crash and reported it (Sentry, 3 Nov,
// "Cannot read properties of undefined (reading 'ARAgingReport')"). Left
// alone, the import rejects with Vite's own error, which lazyWithRetry and
// the boundaries already treat as "updating" while the reload lands.
window.addEventListener('vite:preloadError', () => {
  tryRecoverFromChunkError();
});

// Catch dynamic-import failures that surface as unhandled promise rejections
// (e.g. an idle prefetch of a removed chunk after a deploy). The shared
// recovery routine de-dupes with the boundary so we never double-reload.
window.addEventListener('unhandledrejection', (event) => {
  if (isChunkLoadError(event.reason)) {
    event.preventDefault();
    tryRecoverFromChunkError();
  }
});

// Same idea for plain script/link load failures (capture phase; these don't
// bubble). Useful when a hashed asset 404s without a JS-thrown error.
window.addEventListener(
  'error',
  (event) => {
    const target = event.target as HTMLScriptElement | HTMLLinkElement | null;
    if (!target) return;
    const isScript = target.tagName === 'SCRIPT';
    const isLink = target.tagName === 'LINK';
    if (!isScript && !isLink) return;
    const url = isScript
      ? (target as HTMLScriptElement).src
      : (target as HTMLLinkElement).href;
    if (url && url.includes('/assets/')) {
      tryRecoverFromChunkError();
    }
  },
  true,
);

let updateInterval: ReturnType<typeof setInterval> | null = null;
let beforeUnloadHandler: (() => void) | null = null;

const cleanupServiceWorker = () => {
  if (updateInterval) {
    clearInterval(updateInterval);
    updateInterval = null;
  }
  if (beforeUnloadHandler) {
    window.removeEventListener('beforeunload', beforeUnloadHandler);
    beforeUnloadHandler = null;
  }
};

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    cleanupServiceWorker();
  });
}

const updateSW = registerSW({
  immediate: true,
  onNeedRefresh() {
    // Activate the waiting SW, then full-navigate so the new index.html
    // (with new hashed chunk URLs) replaces the in-memory document.
    // Guard against a misbehaving SW that keeps reporting "need refresh"
    // without ever taking control — without this, it reloads every 60s.
    // Inside the app this waits for the next page change rather than
    // reloading mid-form; see lib/pending-update.ts.
    deferUpdate(() => {
      if (!tryRecoverFromChunkError()) return false;
      updateSW(true)
        .then(() => window.location.reload())
        .catch(() => window.location.reload());
      return true;
    });
  },
  onOfflineReady() {
    console.log('App ready to work offline');
  },
  onRegisteredSW(_swUrl, registration) {
    if (!registration) return;
    cleanupServiceWorker();
    registration.update().catch((err) => {
      console.warn('SW update check failed:', err);
    });
    updateInterval = setInterval(() => {
      registration.update();
    }, 60 * 1000);
    beforeUnloadHandler = () => {
      if (updateInterval) {
        clearInterval(updateInterval);
        updateInterval = null;
      }
    };
    window.addEventListener('beforeunload', beforeUnloadHandler);
  },
  onRegisterError() {
    // Don't reload on SW registration errors — the app still works without it,
    // and a reload loop here would lock users out entirely.
    console.warn('SW registration failed');
  },
});

// The session check's two reads leave now, not after the language file
// below, which saves a round trip on every start. Same condition as the
// check itself: a signed-in hint, and not the reset-password page.
if (
  hasStoredSession() &&
  !window.location.pathname.includes('/reset-password')
) {
  startSessionReads();
}

// The page renders once its language can be shown (see i18nReady); the
// loader in index.html covers the wait. A language file that will not load
// after a deploy is a stale chunk like any other, and is recovered the same
// way; otherwise the app renders with whatever did load.
void i18nReady
  .catch((err) => {
    if (isChunkLoadError(err) && tryRecoverFromChunkError()) return 'reloading';
  })
  .then((state) => {
    if (state === 'reloading') return;
    createRoot(document.getElementById('root')!).render(
      <StrictMode>
        <AppErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <I18nextProvider i18n={i18n}>
              <QueryProvider>
                <LanguageProvider>
                  <App />
                </LanguageProvider>
              </QueryProvider>
            </I18nextProvider>
          </Suspense>
        </AppErrorBoundary>
      </StrictMode>,
    );
  });

// Once the app has stayed up for a few seconds without the error boundary
// triggering another reload, clear the one-shot guard so a future, unrelated
// stale-chunk error can still self-heal. A genuine reload loop never gets here
// because the same error fires again before this timer elapses.
window.setTimeout(() => clearReloadGuard(), 5_000);
